'use client';

import { useState, useRef, useEffect, useCallback, useId } from 'react';

export interface Port {
  unlocode: string;
  name: string;
}

export function PortInput({ label, placeholder, value, onChange }: {
  label: string;
  placeholder: string;
  value: Port | null;
  onChange: (port: Port | null) => void;
}) {
  const [draft, setDraft] = useState('');
  const [suggestions, setSuggestions] = useState<Port[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const query = value ? `${value.name} (${value.unlocode})` : draft;
  const showSuggestions = !value && open && suggestions.length > 0;

  const cancelRequest = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    requestRef.current?.abort();
  }, []);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        cancelRequest();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => {
      cancelRequest();
      document.removeEventListener('mousedown', handleOutside);
    };
  }, [cancelRequest]);

  useEffect(() => {
    if (value) cancelRequest();
  }, [value, cancelRequest]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextQuery = event.target.value;
    cancelRequest();
    setDraft(nextQuery);
    setSuggestions([]);
    setOpen(false);
    setActiveIdx(-1);
    if (value) onChange(null);
    if (nextQuery.trim().length < 2) return;

    const controller = new AbortController();
    requestRef.current = controller;
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`/api/schedules/ports?q=${encodeURIComponent(nextQuery)}`, {
          signal: controller.signal,
        });
        if (!response.ok) return;
        const data: { ports?: Port[] } = await response.json();
        if (controller.signal.aborted) return;
        const ports = Array.isArray(data.ports) ? data.ports : [];
        setSuggestions(ports);
        setOpen(ports.length > 0);
      } catch {
        // Typing, dismissal and unmount cancel requests; a failed search stays empty.
      }
    }, 250);
  };

  const handleSelect = (port: Port) => {
    cancelRequest();
    setDraft('');
    setSuggestions([]);
    setOpen(false);
    setActiveIdx(-1);
    onChange(port);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      cancelRequest();
      setOpen(false);
      return;
    }
    if (!showSuggestions) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIdx(index => Math.min(index + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIdx(index => Math.max(index - 1, -1));
    } else if (event.key === 'Enter' && activeIdx >= 0) {
      event.preventDefault();
      handleSelect(suggestions[activeIdx]);
    }
  };

  return (
    <div className="schedules-port-input-wrap" ref={wrapRef}>
      <label className="schedules-port-label" htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        className="schedules-port-input"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showSuggestions}
        aria-controls={showSuggestions ? `${inputId}-ports` : undefined}
        aria-activedescendant={showSuggestions && activeIdx >= 0 ? `${inputId}-port-${activeIdx}` : undefined}
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onBlur={() => { cancelRequest(); setOpen(false); }}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
      />
      {showSuggestions && (
        <div className="port-autocomplete" role="listbox" id={`${inputId}-ports`} aria-label={`${label} suggestions`}>
          {suggestions.map((port, index) => (
            <div
              key={port.unlocode}
              id={`${inputId}-port-${index}`}
              role="option"
              aria-selected={index === activeIdx}
              className={`port-option${index === activeIdx ? ' port-option--active' : ''}`}
              onMouseDown={event => event.preventDefault()}
              onClick={() => handleSelect(port)}
            >
              <span>{port.name}</span>
              <span className="port-option-code">{port.unlocode}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
